"use client";

import { useEffect, useRef } from "react";
import { useSearchParams, usePathname } from "next/navigation";
import { useReportWebVitals } from "next/web-vitals";
import { capture } from "@/lib/analytics/events";
import { readShareArrival } from "@/lib/share";

const TRACKED_WEB_VITALS = new Set(["LCP", "CLS", "INP", "FCP", "TTFB"]);

/**
 * Fire-once-per-view trackers for things a server component knows and the
 * browser does not.
 *
 * The board, a job page and every SEO landing page are server-rendered, so the
 * only honest source for "how many results did this search return" or "which
 * org was this" is the server. These components take that truth as props and
 * emit the event from the client, where PostHog lives.
 *
 * The signature ref is the whole trick: Next reuses the same component instance
 * across navigations within a segment, so a naive mount effect fires once and
 * then goes silent for every subsequent search. Keying on the values means one
 * event per distinct view, and never a duplicate on a re-render.
 */
function useFireOnce(signature: string, fire: () => void) {
  const last = useRef<string | null>(null);
  useEffect(() => {
    if (last.current === signature) return;
    last.current = signature;
    fire();
    // `fire` is redefined every render; the signature is the real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
}

/**
 * A search happened. Browsing the unfiltered board is not a search — no query,
 * no filters, no event, or every pageview would count as one and the step is
 * meaningless.
 *
 * No search terms or filter values are sent, only the shape: whether there was
 * a query, how many filters, and how many results came back. The app strips
 * query strings from every URL it reports; an event carrying `q` would walk
 * straight around that decision.
 */
export function TrackSearch({
  hasQuery,
  filters,
  results,
}: {
  hasQuery: boolean;
  filters: number;
  results: number;
}) {
  const active = hasQuery || filters > 0;
  useFireOnce(`${hasQuery}|${filters}|${results}`, () => {
    if (!active) return;
    capture("job_search", { has_query: hasQuery, filters, results });
    if (filters > 0) capture("search_filter_apply", { filters });
  });
  return null;
}

/**
 * A job detail page was opened.
 *
 * Two recipients, on purpose. PostHog gets the product event (consent-gated,
 * anonymous). Our own database gets a first-party signal for SIGNED-IN members
 * only — which roles someone actually opens is what lets their lifecycle mail
 * be about their corner of finance instead of "new jobs are live"
 * (src/app/api/signals/job-view/route.ts, disclosed in /privacy). The server
 * ignores it when nobody is signed in and keeps one row per job per day.
 */
export function TrackJobView({ jobId, orgKey }: { jobId: number; orgKey: string }) {
  useFireOnce(String(jobId), () => {
    capture("job_view", { job_id: jobId, org_key: orgKey });
    // After the event, never in front of it: a signal is the least important
    // thing happening on this page.
    window.setTimeout(() => {
      void fetch("/api/signals/job-view", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jobId }),
        keepalive: true,
      }).catch(() => {});
    }, 1500);
  });
  return null;
}

/**
 * An SEO landing page was opened. These pages are the whole acquisition
 * strategy, so "did the location pages bring anyone who converts" needs to be a
 * measurable question rather than an argument.
 */
export function TrackSeoLanding({
  surface,
}: {
  surface: "location" | "category" | "organization" | "guide";
}) {
  useFireOnce(surface, () => capture("seo_landing_view", { surface }));
  return null;
}

/** The post-a-job landing page was opened — the top of the employer funnel. */
export function TrackPostJobView() {
  useFireOnce("post_job_view", () => capture("post_job_view"));
  return null;
}

/**
 * Someone arrived on a shared link.
 *
 * Mounted once in the root layout, because a share can land on ANY page — a
 * job, a filtered search, a portfolio — and a per-page tracker would measure
 * whichever pages we remembered to instrument rather than sharing itself.
 *
 * Reads the UTM parameters rather than a bespoke flag, so a link that someone
 * copied out of WhatsApp and pasted into Slack still counts. Fires nothing at
 * all for organic traffic: an "unknown" bucket that swallows every normal visit
 * would make the conversion rate meaningless.
 */
export function TrackShareVisit() {
  const search = useSearchParams();
  const arrival = readShareArrival(search.toString());
  useFireOnce(arrival ? `${arrival.surface}|${arrival.channel}` : "", () => {
    if (arrival) capture("share_visit", arrival);
  });
  return null;
}

/**
 * Real-user Core Web Vitals (RUM), reported once per metric per page load.
 * Mounted once in the root layout for the same reason as `TrackShareVisit`:
 * every page needs it, and a per-page tracker would only measure the pages we
 * remembered to instrument. `useReportWebVitals` fires each metric exactly
 * once as it finalizes (LCP/CLS/INP settle late — on visibility change or
 * page unload — the others fire early), so no fire-once dedup is needed here.
 */
export function TrackWebVitals() {
  const pathname = usePathname();
  useReportWebVitals((metric) => {
    if (!TRACKED_WEB_VITALS.has(metric.name)) return;
    capture("web_vital", {
      metric: metric.name as "LCP" | "CLS" | "INP" | "FCP" | "TTFB",
      value: metric.value,
      rating: metric.rating,
      path: pathname ?? "",
    });
  });
  return null;
}
